import { useMemo } from 'react';
import { Typography } from 'antd';

import { useQuery } from '@tanstack/react-query';

import { getColumnsSearchFieldsChanged } from 'services/reporting';

export const getProductHistorySchemaKey = () => {
  return ['get_product_history_detail_schema'];
};

export const useGetColumnsSearchFieldsChange = () => {
  const queries = useQuery({
    queryKey: getProductHistorySchemaKey(),
    queryFn: async () => {
      const response = await getColumnsSearchFieldsChanged();
      return response?.data?.modules;
    },
    staleTime: Infinity,
  });

  return {
    ...queries,
  };
};

export const useGetFieldsChange = () => {
  const { data } = useGetColumnsSearchFieldsChange();

  const treeData = useMemo(() => mappingTreeData(data), [JSON.stringify(data)]);

  const flattenProperties = useMemo(
    () => handleFlattenProperties(data),
    [JSON.stringify(data)]
  );

  const getPropertyData = (fieldName) => {
    return { ...flattenProperties[fieldName] } || null;
  };

  return { treeData, getPropertyData };
};

const mappingTreeData = (propertiesList) => {
  const recursionFunc = (module) => {
    const treeData = [];
    module?.forEach((property) => {
      const childFields =
        property?.moduleProperties || property?.childProperties;

      const hasChildField = childFields?.length > 0;

      const node = getNodeTreeData(property, hasChildField);

      if (hasChildField) {
        treeData?.push({
          ...node,
          children: recursionFunc(childFields),
        });
      } else {
        treeData?.push({ ...node });
      }
    });
    return treeData;
  };

  const data = recursionFunc(propertiesList, null);

  return data;
};

const getNodeTreeData = (property, isNestedProperty) => {
  const { moduleDisplayName, fieldFullPath, propertyDisplayName } = property;

  const displayName = moduleDisplayName || propertyDisplayName;
  return {
    title: isNestedProperty ? (
      <Typography.Title
        level={5}
        style={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.65)', marginBottom: 0 }}
      >
        {displayName}
      </Typography.Title>
    ) : (
      <Typography.Text strong>{propertyDisplayName}</Typography.Text>
    ),
    isLeaf: isNestedProperty ? false : true,
    selectable: isNestedProperty ? false : true,
    data: property,
    value: fieldFullPath || moduleDisplayName,
  };
};

export const handleFlattenProperties = (propertiesList) => {
  const properties = {};
  const flattenProperties = (fields, parentDisplayName) => {
    fields?.forEach((field) => {
      const fullPathDisplayName = `${parentDisplayName}${
        parentDisplayName && field.propertyDisplayName ? ' - ' : ''
      }${field.propertyDisplayName}`;

      if (field?.childProperties?.length > 0) {
        flattenProperties(field?.childProperties, fullPathDisplayName);
      } else {
        properties[field?.fieldFullPath] = { ...field, fullPathDisplayName };
      }
    });
  };

  propertiesList?.forEach((module) => {
    const childrenFields = module?.moduleProperties || module?.childProperties;
    flattenProperties(childrenFields, module?.moduleDisplayName);
  });

  return properties;
};
